import { FLOWS } from 'universal/ucs';
import { FLOW_STEPS } from '@/constants/flow-steps';
import COMMON_FPTI_TAGS from './common';

const flowName = FLOWS.ENROLL_CARD;
const {
  ELIGIBLE_STEP,
  INELIGIBLE_STEP,
  CARD_ENROLLING_STEP,
  BDMC_XSELL_STEP,
  CARD_SUCCESS_STEP,
} = FLOW_STEPS.ENROLL_CARD;

const { needMoreDataImpressionTags, manualReviewImpressionTags } = COMMON_FPTI_TAGS;

const eligibleLandingPageTag = `${flowName}:${ELIGIBLE_STEP}`;
const ineligibleLandingPageTag = `${flowName}:${INELIGIBLE_STEP}`;
const cardEnrollingPageTag = `${flowName}:${CARD_ENROLLING_STEP}`;
const bdmcXsellPageTag = `${flowName}:${BDMC_XSELL_STEP}`;
const cardSuccessPageTag = `${flowName}:${CARD_SUCCESS_STEP}`;

/**
 * Enum for Enroll Card API call names used in FPTI tracking.
 * @enum {string}
 */
const EnrollCardApiCalls = {
  /** Card management flow navigation API call name. */
  CARD_MANAGEMENT_NAVIGATION: 'card_management_navigation',
  /** Initial enrollment API call name. */
  INIT_ENROLLMENT: 'init_enrollment',
  /** Initial enrollment pre-eligibility API call name. */
  INIT_ENROLLMENT_PRE_ELIGIBILITY: 'init_enrollment_pre_eligibility',
  /** PS Studio content API call name. */
  PS_STUDIO_CONTENT: 'ps_studio_content',
};

/**
 * Enum for Enroll Card FPTI event names.
 * @enum {string}
 */
const EnrollCardEvents = {
  /** Eligible landing screen is shown. */
  ELIGIBLE_LANDING_SHOWN: 'ucs_enroll_landing_screen_shown',
  /** Value props screen is triggered/loaded. */
  VALUE_PROPS_SCREEN_TRIGGERED: 'ucs_value_props_screen_triggered',
  /** Value props screen content is shown after PS Studio loads. */
  VALUE_PROPS_CONTENT_SHOWN: 'ucs_value_props_screen_content_shown',
  /** Value props setup PPDC button is clicked. */
  VALUE_PROPS_SETUP_PPDC_PRESSED: 'ucs_value_props_screen_setup_ppdc_pressed',
  /** Value props back button is pressed. */
  VALUE_PROPS_BACK_PRESSED: 'ucs_value_props_screen_back_pressed',
  /** Value props dismiss button is pressed. */
  VALUE_PROPS_DISMISS_PRESSED: 'ucs_value_props_screen_dismiss_pressed',
  /** Value props terms link is pressed. */
  VALUE_PROPS_TERMS_LINK_PRESSED: 'ucs_value_props_screen_terms_link_pressed',
  /** API data requested. */
  API_DATA_REQUESTED: 'ucs_api_data_requested',
  /** API response received. */
  API_RESPONSE_RECEIVED: 'ucs_api_response_received',
  /** Get PPDC button is clicked. */
  GET_PPDC_CLICKED: 'ucs_enroll_landing_set_up_pressed',
  /** Ineligible landing screen is shown. */
  INELIGIBLE_LANDING_SHOWN: 'ucs_enroll_ineligible_screen_shown',
  /** Card enrolling spinner is shown. */
  CARD_ENROLLING_SPINNER_SHOWN: 'ucs_enroll_set_up_spinner_shown',
  /** Card enrolled successfully. */
  CARD_ENROLLED_SUCCESS: 'ucs_enroll_success_occurred',
  /** Need more data screen is shown. */
  NEED_MORE_DATA_SHOWN: 'ucs_enroll_need_more_data_screen_shown',
  /** Manual review screen is shown. */
  MANUAL_REVIEW_SHOWN: 'ucs_enroll_manual_review_screen_shown',
  /** BDMC cross-sell screen is shown. */
  BDMC_XSELL_SHOWN: 'ucs_bdmc_cross_sell_screen_shown',
  /** About BDMC link is clicked. */
  ABOUT_BDMC_CLICKED: 'ucs_bdmc_cross_sell_about_bdmc_pressed',
  /** Request BDMC button is clicked. */
  REQUEST_BDMC_CLICKED: 'ucs_bdmc_cross_sell_request_bdmc_pressed',
  /** Maybe later link is clicked. */
  MAYBE_LATER_CLICKED: 'ucs_bdmc_cross_sell_maybe_later_pressed',
  /** Go to summary button is clicked. */
  GO_TO_SUMMARY_CLICKED: 'ucs_enroll_go_to_summary_pressed',
  /** Loyalty Checkbox is clicked. */
  LOYALTY_CHECKBOX_CLICKED: 'ucs_enroll_loyalty_checkbox_clicked',
  /** Enroll card success screen is shown. */
  SUCCESS_SCREEN_SHOWN: 'ucs_enroll_card_success_screen_shown',
  /** Done button is pressed on enroll card success screen. */
  SUCCESS_DONE_PRESSED: 'ucs_enroll_card_success_done_pressed',
  /** Dismiss button is pressed on enroll card success screen. */
  SUCCESS_DISMISS_PRESSED: 'ucs_enroll_card_success_dismiss_pressed',
};

const ELIGIBLE_LANDING_TAGS = {
  eligibleLandingImpressionTags: {
    page: eligibleLandingPageTag,
    eventName: EnrollCardEvents.ELIGIBLE_LANDING_SHOWN,
  },
  valuePropsScreenTriggeredTags: {
    page: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_SCREEN_TRIGGERED,
  },
  valuePropsContentShownTags: {
    page: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_CONTENT_SHOWN,
  },
  valuePropsSetupPpdcTags: {
    link: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_SETUP_PPDC_PRESSED,
  },
  valuePropsBackTags: {
    link: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_BACK_PRESSED,
  },
  valuePropsDismissTags: {
    link: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_DISMISS_PRESSED,
  },
  valuePropsTermsLinkTags: {
    link: eligibleLandingPageTag,
    eventName: EnrollCardEvents.VALUE_PROPS_TERMS_LINK_PRESSED,
  },
  getPPDCClickTags: {
    link: 'getCardButton',
    eventName: EnrollCardEvents.GET_PPDC_CLICKED,
  },
  loyaltyCheckBoxClickTags: {
    page: eligibleLandingPageTag,
    eventName: EnrollCardEvents.LOYALTY_CHECKBOX_CLICKED,
  }
};

const INELIGIBLE_LANDING_TAGS = {
  ineligibleLandingImpressionTags: {
    page: ineligibleLandingPageTag,
    eventName: EnrollCardEvents.INELIGIBLE_LANDING_SHOWN,
  },
};

const CARD_ENROLLING_TAGS = {
  cardEnrollingImpressionTags: {
    page: cardEnrollingPageTag,
    eventName: EnrollCardEvents.CARD_ENROLLING_SPINNER_SHOWN,
  },
  cardEnrolledSuccessActivityTags: {
    page: cardEnrollingPageTag,
    eventName: EnrollCardEvents.CARD_ENROLLED_SUCCESS,
  },
};

const NEED_MORE_DATA_TAGS = {
  needMoreDataImpressionTags: {
    eventName: EnrollCardEvents.NEED_MORE_DATA_SHOWN,
    ...needMoreDataImpressionTags(flowName),
  },
};

const MANUAL_REVIEW_TAGS = {
  manualReviewImpressionTags: {
    eventName: EnrollCardEvents.MANUAL_REVIEW_SHOWN,
    ...manualReviewImpressionTags(flowName),
  },
};

const BDMC_XSELL_TAGS = {
  bdmcXsellImpressionTags: {
    page: bdmcXsellPageTag,
    eventName: EnrollCardEvents.BDMC_XSELL_SHOWN,
  },
  aboutBDMCClickTags: {
    link: 'aboutBDMCLink',
    eventName: EnrollCardEvents.ABOUT_BDMC_CLICKED,
  },
  requestBDMCClickTags: {
    link: 'requestBDMCButton',
    eventName: EnrollCardEvents.REQUEST_BDMC_CLICKED,
  },
  maybeLaterClickTags: {
    link: 'maybeLaterLink',
    eventName: EnrollCardEvents.MAYBE_LATER_CLICKED,
  },
};

const CARD_SUCCESS_TAGS = {
  cardSuccessImpressionTags: {
    page: cardSuccessPageTag,
    eventName: EnrollCardEvents.SUCCESS_SCREEN_SHOWN,
  },
  cardSuccessDoneClickTags: {
    link: 'doneButton',
    eventName: EnrollCardEvents.SUCCESS_DONE_PRESSED,
  },
  cardSuccessDismissClickTags: {
    link: cardSuccessPageTag,
    eventName: EnrollCardEvents.SUCCESS_DISMISS_PRESSED,
  },
};

const COMMON_TAGS = {
  summaryClickTags: {
    link: 'goToSummaryButton',
    eventName: EnrollCardEvents.GO_TO_SUMMARY_CLICKED,
  },
};

const ENROLL_CARD_FPTI_TAGS = {
  ...ELIGIBLE_LANDING_TAGS,
  ...INELIGIBLE_LANDING_TAGS,
  ...CARD_ENROLLING_TAGS,
  ...NEED_MORE_DATA_TAGS,
  ...MANUAL_REVIEW_TAGS,
  ...BDMC_XSELL_TAGS,
  ...CARD_SUCCESS_TAGS,
  ...COMMON_TAGS,
};

export { EnrollCardEvents, EnrollCardApiCalls };
export default ENROLL_CARD_FPTI_TAGS;